import React from 'react';
import { StatusBadge } from '../../ui/StatusBadge';
import { ProcessedUser } from '../../../hooks/useProcessedUsers';
import { USER_TABLE_COLUMNS } from '../constants';

interface UserAnalysisTableProps {
  filteredSortedUsers: ProcessedUser[];
  paginatedUsers: ProcessedUser[];
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  totalPages: number;
  sort: { key: string | null; dir: 'asc' | 'desc' };
  onSort: (key: string) => void;
}

export const UserAnalysisTable: React.FC<UserAnalysisTableProps> = ({
  filteredSortedUsers,
  paginatedUsers,
  currentPage,
  setCurrentPage,
  pageSize,
  totalPages,
  sort,
  onSort
}) => {
  const columnHideClass: Record<string, string> = {
    email: 'hidden md:table-cell',
    lastUsed: 'hidden md:table-cell',
    projects: 'hidden lg:table-cell',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="px-2 sm:px-4 py-1.5 sm:py-2 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center gap-2">
        <h2 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
          User Analysis ({filteredSortedUsers.length} users)
        </h2>
        {totalPages > 1 && (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <span>Page {currentPage} of {totalPages}</span>
            <div className="flex gap-1">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded disabled:opacity-50 hover:bg-slate-200 dark:hover:bg-slate-700"
              >
                &larr;
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded disabled:opacity-50 hover:bg-slate-200 dark:hover:bg-slate-700"
              >
                &rarr;
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="overflow-x-auto custom-scrollbar">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-2 sm:px-3 py-1 sm:py-1.5 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                #
              </th>
              {USER_TABLE_COLUMNS.map(col => (
                <th
                  key={col.key}
                  className={`px-2 sm:px-3 py-1 sm:py-1.5 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 ${columnHideClass[col.key] || ''}`}
                  onClick={() => onSort(col.key)}
                >
                  {col.header}
                  {sort.key === col.key && (
                    <span className="ml-1">
                      {sort.dir === 'asc' ? '\u25B2' : '\u25BC'}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {paginatedUsers.map((user, idx) => (
              <tr key={idx} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900'}>
                <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                  {(currentPage - 1) * pageSize + idx + 1}
                </td>
                <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs font-medium text-gray-900 dark:text-white">
                  {user.name}
                </td>
                <td className="hidden md:table-cell px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                  {user.email}
                </td>
                <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                  <StatusBadge
                    status={user.role}
                    variant={user.role === 'owner' ? 'amber' : 'slate'}
                  />
                </td>
                <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                  <StatusBadge
                    status={user.status}
                    variant={user.status === 'Active' ? 'emerald' : 'slate'}
                  />
                </td>
                <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                  {user.projectCount}
                </td>
                <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                  {user.apiKeyCount}
                </td>
                <td className="hidden md:table-cell px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                  {user.lastUsed || 'Never'}
                </td>
                <td className="hidden lg:table-cell px-2 sm:px-3 py-1 sm:py-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <div className="max-w-[150px] sm:max-w-xs">
                    {user.projects.join(', ') || 'No projects'}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
